import React from 'react'
import { useQueryClient } from 'react-query'
import {
  Flex,
  Row,
  Col,
  Input,
  Table,
  Button,
  Typography,
  Popconfirm,
} from 'antd'
import { FormattedMessage } from 'react-intl'
import Loading from '../components/Loading'
import { searchList } from '../components/SubsystemTable'
import { useGlobalSearchContext } from './GlobalSearchContext'
import {
  useBulkUpdateAssignedStations,
  useResetStationAdjustments,
  useComponentStationMappings,
  COMPONENT_STATION_MAPPINGS_KEY,
} from '../hooks/Stations'
import { useSalesCodeConstraints } from '../hooks/SalesCodes'
import AddStationGroupModal from './AddStationGroupModal'

const { Title } = Typography

const SearchInput = ({ value, onChange, intl }) => {
  return (
    <Input
      autoFocus
      allowClear
      className="stations-search"
      placeholder={intl.formatMessage({
        id: 'globalSearch.stations.searchInputLabel',
      })}
      value={value}
      onChange={onChange}
    />
  )
}

const Stations = ({ stations, isLoading, onStationSelect, editing, intl }) => {
  const pageSize = 10
  const [searchString, setSearchString] = React.useState('')
  const assignButtonLabel = intl.formatMessage({
    id: 'globalSearch.stations.assignButtonLabel',
  })
  const columns = [
    {
      title: intl.formatMessage({ id: 'globalSearch.stations.stationIDTitle' }),
      dataIndex: 'station_id',
      key: 'station_id',
    },
    {
      title: intl.formatMessage({
        id: 'globalSearch.stations.departmentTitle',
      }),
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: intl.formatMessage({ id: 'globalSearch.stations.teamTitle' }),
      dataIndex: 'team',
      key: 'team',
    },
    {
      title: intl.formatMessage({ id: 'globalSearch.stations.zoneTitle' }),
      dataIndex: 'zone',
      key: 'zone',
    },
    {
      render: (text, record) => (
        <Button
          onClick={() => onStationSelect(record)}
          type="primary"
          disabled={!editing}
        >
          {assignButtonLabel}
        </Button>
      ),
    },
  ]
  const availableStationsTitle = intl.formatMessage({
    id: 'globalSearch.stations.availableStationsTitle',
  })
  return isLoading ? (
    <Loading useBackgroundColor={false} />
  ) : (
    <Flex vertical={true}>
      <Row>
        <Col span={12}>
          <Title level={5}>{availableStationsTitle}:</Title>
        </Col>
        <Col span={12}>
          <SearchInput
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
            intl={intl}
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Table
            data-testid={'station-results'}
            size="small"
            columns={columns}
            dataSource={searchList({
              items: stations?.map((item) => ({
                ...item,
                key: item.station_id,
              })),
              searchString,
              fields: [['station_id'], ['team'], ['zone'], ['department']],
            })}
            loading={isLoading}
            pagination={{
              pageSize: pageSize,
              hideOnSinglePage: true,
              showSizeChanger: false,
            }}
            rowKey={(record) =>
              [
                record.station_id,
                record.department,
                record.team,
                record.zone,
              ].join('-')
            }
          />
        </Col>
      </Row>
    </Flex>
  )
}

const StationTab = ({
  plant_name,
  plantDisplayName,
  plantLabel,
  makeModel,
  stations,
  isLoading,
  intl,
}) => {
  const globalSearchContext = useGlobalSearchContext()
  const bulkUpdateAssignedStations = useBulkUpdateAssignedStations()
  const resetStationAdjustments = useResetStationAdjustments()
  const queryClient = useQueryClient()

  const { selection } = globalSearchContext
  const defaultSalesCodes = makeModel?.default_sales_codes

  const { mappings } = useComponentStationMappings({
    componentAlias: selection.componentAlias,
    makeModelId: makeModel?.id,
    plantName: plant_name,
  })

  // Resolve sales-code descriptions (same source the group modal uses) so both the create and edit
  // flows can show "Description (CODE)".
  const { salesCodeConstraints } = useSalesCodeConstraints({
    makeModelID: makeModel?.id,
  })
  const descriptionBySalesCode = React.useMemo(() => {
    const map = {}
    ;(salesCodeConstraints ?? []).forEach((constraint) => {
      map[constraint.sales_code.name] = constraint.sales_code.description
    })
    return map
  }, [salesCodeConstraints])

  // A component has per-sales-code mappings when it carries more than one mapping, or any mapping on
  // a non-default sales code (i.e. it's no longer just the single default/catch-all mapping).
  const hasPerSalesCodeMappings = React.useMemo(() => {
    if (!mappings || mappings.length === 0) return false
    return (
      mappings.length > 1 ||
      mappings.some((m) => m.sales_code !== defaultSalesCodes)
    )
  }, [mappings, defaultSalesCodes])

  // True when any current mapping came from a user adjustment (a split, or an edited default) — i.e.
  // there is something to reset.
  const hasAnyAdjustment = (mappings ?? []).some((m) => m.adjusted)

  // Editable per-sales-code list state. `rows` are {salesCode, description?}; `assignments` maps
  // salesCode -> station_id. `savedAssignments` is the last-saved baseline used to flag unsaved edits.
  const [editing, setEditing] = React.useState(false)
  const [rows, setRows] = React.useState([])
  const [assignments, setAssignments] = React.useState({})
  const [savedAssignments, setSavedAssignments] = React.useState({})
  const [activeSalesCode, setActiveSalesCode] = React.useState(null)
  const [modalOpen, setModalOpen] = React.useState(false)

  const initFromMappings = React.useCallback(() => {
    if (hasPerSalesCodeMappings) {
      const baseline = Object.fromEntries(
        mappings.map((m) => [m.sales_code, m.station_id]),
      )
      setRows(mappings.map((m) => ({ salesCode: m.sales_code })))
      setAssignments(baseline)
      setSavedAssignments(baseline)
      setActiveSalesCode(mappings[0]?.sales_code ?? null)
      setEditing(true)
    } else {
      setRows([])
      setAssignments({})
      setSavedAssignments({})
      setActiveSalesCode(null)
      setEditing(false)
    }
  }, [hasPerSalesCodeMappings, mappings])

  // Initialize the editable list once per selected component, after its mappings have loaded. The
  // ref guard prevents later refetches (or in-progress edits) from clobbering user input.
  const initializedForRef = React.useRef(null)
  React.useEffect(() => {
    if (mappings === undefined) return
    if (initializedForRef.current === selection.componentAlias) return
    initializedForRef.current = selection.componentAlias
    initFromMappings()
  }, [selection.componentAlias, mappings, initFromMappings])

  const showAddStation =
    !editing &&
    !!defaultSalesCodes &&
    selection.componentSalesCode === defaultSalesCodes

  const onGroupConfirm = (group) => {
    setRows(group.constraints.map((c) => ({ salesCode: c.sales_code.name })))
    setAssignments({})
    setSavedAssignments({})
    setActiveSalesCode(group.constraints[0]?.sales_code.name ?? null)
    setEditing(true)
    setModalOpen(false)
  }

  const cancelEditing = () => {
    if (hasPerSalesCodeMappings) {
      // Revert any unsaved edits back to the saved mappings.
      initFromMappings()
    } else {
      setEditing(false)
      setRows([])
      setAssignments({})
      setActiveSalesCode(null)
    }
  }

  const allMapped =
    rows.length > 0 && rows.every((r) => assignments[r.salesCode])

  // A row is always selected while editing: the explicitly-picked one, otherwise the first row. This
  // keeps the first row selected at start and re-selects it once every row is mapped (so editing a
  // fully-mapped list never leaves the user with nothing to assign to).
  const activeRow = activeSalesCode ?? rows[0]?.salesCode ?? null

  const onStationSelect = (record) => {
    // Stations can only be assigned through the per-sales-code mapping flow.
    if (!editing || !activeRow) return
    const nextAssignments = {
      ...assignments,
      [activeRow]: record.station_id,
    }
    setAssignments(nextAssignments)
    // Auto-advance to the next still-unmapped sales code; fall back to the first row when all mapped.
    const nextUnmapped = rows.find((r) => !nextAssignments[r.salesCode])
    setActiveSalesCode(
      nextUnmapped ? nextUnmapped.salesCode : rows[0]?.salesCode ?? null,
    )
  }

  const onSave = () => {
    bulkUpdateAssignedStations.mutate(
      {
        componentAlias: selection.componentAlias,
        plantName: plant_name,
        makeModelId: makeModel.id,
        assignments: rows.map((r) => ({
          sales_code: r.salesCode,
          station_id: assignments[r.salesCode],
        })),
      },
      {
        onSuccess: refreshMappings,
      },
    )
  }

  // Re-initialize the editable list from the freshly persisted mappings.
  function refreshMappings() {
    initializedForRef.current = null
    queryClient.invalidateQueries([
      COMPONENT_STATION_MAPPINGS_KEY,
      selection.componentAlias,
      makeModel.id,
      plant_name,
    ])
  }

  // The component's current default station (post-adjustment), falling back to the search result.
  const currentStation = mappings?.[0]?.station_id ?? selection.componentStation

  // Feature 1: edit the single default-sales-code station via the editable list (one row).
  const onEditDefault = () => {
    const baseline = currentStation
      ? { [defaultSalesCodes]: currentStation }
      : {}
    setRows([{ salesCode: defaultSalesCodes }])
    setAssignments(baseline)
    setSavedAssignments(baseline)
    setActiveSalesCode(defaultSalesCodes)
    setEditing(true)
  }

  // Feature 2: drop the group split and revert to the default mapping.
  const onReset = () => {
    resetStationAdjustments.mutate(
      {
        componentAlias: selection.componentAlias,
        plantName: plant_name,
        makeModelId: makeModel.id,
      },
      {
        onSuccess: refreshMappings,
      },
    )
  }

  const unmappedPlaceholder = intl.formatMessage({
    id: 'globalSearch.stations.unmappedPlaceholder',
  })

  // Reset only undoes user adjustments. A component that ships pre-split in the package (multiple
  // deployed rows, none adjusted) has nothing to reset, so the button is disabled there.
  const resetButton = (
    <Popconfirm
      title={intl.formatMessage({ id: 'globalSearch.stations.resetConfirm' })}
      okText={intl.formatMessage({
        id: 'globalSearch.stations.resetToDefaultButton',
      })}
      cancelText={intl.formatMessage({ id: 'forms.cancel' })}
      onConfirm={onReset}
      disabled={!hasAnyAdjustment}
    >
      <Button
        danger
        disabled={!hasAnyAdjustment}
        loading={resetStationAdjustments.isLoading}
        data-testid="reset-station-mappings-button"
      >
        <FormattedMessage id="globalSearch.stations.resetToDefaultButton" />
      </Button>
    </Popconfirm>
  )

  return (
    <>
      {editing ? (
        <div className={'assigned-station station-mapping'}>
          <Row>
            <Col span={24}>
              <Title level={5}>
                <FormattedMessage id="globalSearch.stations.mapSalesCodesTitle" />
              </Title>
            </Col>
          </Row>
          {rows.map((row) => {
            const code = row.salesCode
            const isActive = code === activeRow
            const mappedStation = assignments[code]
            const description = descriptionBySalesCode[code]
            const isChanged = assignments[code] !== savedAssignments[code]
            const className = [
              'sales-code-row',
              isActive ? 'active' : '',
              isChanged ? 'changed' : '',
            ]
              .filter(Boolean)
              .join(' ')
            return (
              <Row
                key={code}
                className={className}
                onClick={() => setActiveSalesCode(code)}
                data-testid={`sales-code-row-${code}`}
              >
                <Col span={12}>
                  {description ? `${description} (${code})` : code}
                </Col>
                <Col span={12} className={'mapped-station'}>
                  {mappedStation || (
                    <span className={'unmapped'}>{unmappedPlaceholder}</span>
                  )}
                  {isChanged && (
                    <span className={'unsaved-badge'}>
                      <FormattedMessage id="globalSearch.stations.unsavedLabel" />
                    </span>
                  )}
                </Col>
              </Row>
            )
          })}
          <Flex justify="flex-end" gap="small" className={'mapping-actions'}>
            {hasPerSalesCodeMappings && resetButton}
            <Button onClick={cancelEditing}>
              <FormattedMessage id="globalSearch.stations.cancelMappingButton" />
            </Button>
            <Button
              type="primary"
              disabled={!allMapped}
              loading={bulkUpdateAssignedStations.isLoading}
              onClick={onSave}
              data-testid="save-station-mappings-button"
            >
              <FormattedMessage id="globalSearch.stations.saveMappingsButton" />
            </Button>
          </Flex>
        </div>
      ) : (
        <Flex className={'assigned-station'} align="center" gap="middle">
          <Title level={5} className={'assigned-station-title'}>
            <FormattedMessage id="globalSearch.stations.stationMappingTitle" />
          </Title>
          <span className={'assigned-station-id'}>{currentStation}</span>
          {showAddStation && (
            <>
              <Button data-testid="edit-station-button" onClick={onEditDefault}>
                <FormattedMessage id="globalSearch.stations.editStationButton" />
              </Button>
              <Button
                type="primary"
                data-testid="add-station-button"
                onClick={() => setModalOpen(true)}
              >
                <FormattedMessage id="globalSearch.stations.mapBySalesCodeButton" />
              </Button>
            </>
          )}
          {hasAnyAdjustment && resetButton}
          <span className={'assigned-station-plant'}>
            {plantLabel}:&nbsp;{plantDisplayName}
          </span>
        </Flex>
      )}

      <Stations
        stations={stations}
        isLoading={isLoading}
        onStationSelect={onStationSelect}
        editing={editing}
        intl={intl}
      />

      <AddStationGroupModal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onConfirm={onGroupConfirm}
        makeModelId={makeModel?.id}
      />
    </>
  )
}

export default StationTab
